import React, { useState } from 'react';

interface InterviewCardProps {
  title: string;
  href: string;
  source: 'YouTube' | 'Spotify';
  description: string[];
  date: string;
  imageSrc: string;
  imageAlt: string;
  host?: string;
  imageClassName?: string;
}

const InterviewCard: React.FC<InterviewCardProps> = ({
  title,
  href,
  source,
  description,
  date,
  imageSrc,
  imageAlt,
  host,
  imageClassName,
}) => {
  const [imageFailed, setImageFailed] = useState(false);

  return (
    <article className="group flex flex-col md:flex-row md:items-stretch overflow-hidden rounded-xl border border-transparent transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-xl hover:border-[#d9e3df]">
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-full md:w-[38%] relative overflow-hidden">
        {!imageFailed ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
            <img
              src={imageSrc}
              alt={imageAlt}
              className={`w-full h-full object-contain block transition-transform duration-500 group-hover:scale-[1.03] ${imageClassName ?? ''}`}
              onError={() => setImageFailed(true)}
            />
          </a>
        ) : (
          <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
            Image placeholder
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 p-3 md:p-4 flex flex-col justify-between overflow-hidden">
        <div className="flex flex-col justify-between h-full">
          <div className="overflow-hidden">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-start gap-2 hover:text-[#0B6054] transition-colors"
            >
              <h2 className="text-[#1f2d3d] font-bold text-[14px] md:text-[18px] leading-snug md:leading-[1.2] transition-colors duration-300 group-hover:text-[#0B6054]">
                {title}
              </h2>
              <img
                src="/images/Arrow.webp"
                alt=""
                className="w-4 h-4 flex-shrink-0 mt-0.5 opacity-70 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0.5"
                aria-hidden
              />
            </a>

            <div className="flex items-center gap-2 mt-1 text-[12px] md:text-[13px] text-gray-500">
              {source === 'YouTube' ? (
                <>
                  <img src="/images/youtube.webp" alt="" className="w-4 h-4" aria-hidden />
                  <span>YouTube</span>
                </>
              ) : (
                <>
                  <img src="/images/spotify.webp" alt="" className="w-4 h-4" aria-hidden />
                  <span>Spotify</span>
                </>
              )}
            </div>

            <div className="mt-2 space-y-1.5 text-[#667085] text-[14px] md:text-[13px] leading-[20px] font-normal overflow-hidden">
              {description.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            
          </div>

          <p className="mt-2 text-[#101828] text-[13px] md:text-[14px] leading-[20px] font-medium">
            {date}
          </p>
        </div>
      </div>
    </article>
  );
};

const interviews = [
  {
    title: "Rob Bruggeman - Introducing The Wealthy Miner: A Subscription Research Platform For Metals Investors",
    href: "https://youtu.be/8xDoDQQGeeY",
    source: 'YouTube' as const,
    description: [
      "Rob explains his current preference for producers over early-stage explorers, citing the immediate benefits of rising metal prices and the risk of cost inflation for future developments.",
    ],
    date: "07 Apr 2026",
    imageSrc: "/images/interview5.webp",
    imageAlt: "Rob Bruggeman interview thumbnail",
  },
  {
    title: "Gold Could Hit $10,000? Rob Bruggeman on Rising Global Crisis Risks",
    href: "https://www.youtube.com/watch?v=J_DsfyNnhoY",
    source: 'YouTube' as const,
    description: [
      "In this interview, Rob Bruggeman discusses why gold prices could potentially reach $10,000 as global economic uncertainty, geopolitical tensions, and financial market risks continue to rise. Hosted by “Living Your Greatness,” the conversation explores how investors may turn to gold during periods of crisis and instability.",
    ],
    date: "31 Mar 2026",
    imageSrc: "/images/interview4.webp",
    imageAlt: "Rob Bruggeman interview thumbnail",
    imageClassName: "p-3 md:p-4",
  },
  {
    title: "Gold About To Double Again As Financial Crisis Now ‘Inevitable’ | Rob Bruggeman",
    href: "https://youtu.be/kxiCmu3gdRA?si=dnEFx0K4WWyQY7zR",
    source: 'YouTube' as const,
    description: [
      "Rob Bruggeman, Co-Founder of The Wealthy Miner, and Director of AbraSilver Resource Corp., discusses the forces driving gold and commodity prices higher, the global debt and deficit cycle eroding purchasing power, and where he sees the best opportunities for investors right now.",
    ],
    date: "17 Mar 2026",
    imageSrc: "/images/interview3.webp",
    imageAlt: "Rob Bruggeman interview thumbnail",
  },
  {
    title: "Is It Time to Sell Mining Stocks? | Rob Bruggeman (The Wealthy Miner) Interview",
    href: "https://www.youtube.com/watch?v=M4iCcykAaY8",
    source: 'YouTube' as const,
    description: [
      "If you’ve been watching the volatility in the resource sector, you’ve probably asked yourself the same question: is it time to sell mining stocks? In this in-depth interview, Rob Bruggeman of The Wealthy Miner shares his expert perspective on where we are in the current mining cycle, what smart investors should be watching, and how to position a portfolio in uncertain times.",
      "Tune in to hear Rob’s outlook on the future of mining stocks, the warning signs investors shouldn’t ignore, and the strategies he’s using right now inside his own portfolio.",
    ],
    date: "03 Feb 2026",
    imageSrc: "/images/interview1.webp",
    imageAlt: "Is It Time to Sell Mining Stocks - Rob Bruggeman Interview",
  },
  {
    title: "$5,000 gold isn't a lifeline: The Wealthy Miner's Robert Bruggeman on why most resource stocks still don't work",
    href: "https://open.spotify.com/episode/3tbcYqaYJN0ZuQ2YSwasHh",
    source: 'Spotify' as const,
    description: [
      "For investors chasing the gold narrative, this conversation offers a critical reality check. Learn what actually drives sustainable performance in mining equities, how to spot red flags early, and why selectivity matters more than ever in today’s market.",
      "If you’re wondering whether $5,000 gold guarantees profits across the sector — or if you’re trying to separate opportunity from hype — this interview delivers the clarity you need.",
    ],
    date: "03 Feb 2026",
    imageSrc: "/images/interview2.webp",
    imageAlt: "Rob Bruggeman on mining super-cycle - Spotify",
  },
];

const InterviewsSection: React.FC = () => {
  return (
    <section className="bg-[#f5f5f5] py-12 md:py-16">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="space-y-10 md:space-y-12">
          {interviews.map((item, index) => (
            <InterviewCard key={index} {...item} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default InterviewsSection;
